<template>
    <div class="profile-picture-container" v-if="!isSSO">
        <div 
            v-if="imageUrl && !imageError && !loading" 
            class="profile-picture"
            :class="[`size-${size}`, { 'editable': editable }]"
            :style="{ backgroundImage: `url(${imageUrl})` }"
            @click="editable && triggerFileUpload()"
        >
            <img 
                :src="imageUrl" 
                :alt="altText"
                @error="handleImageError"
                class="profile-image"
            />
            <div v-if="editable" class="upload-overlay">
                <span class="upload-text">Modifier</span>
            </div>
        </div>
        <div 
            v-else-if="!loading"
            class="profile-picture profile-picture-fallback"
            :class="[`size-${size}`, { 'editable': editable }]"
            @click="editable && triggerFileUpload()"
        >
            <img 
                src="/img/profil_picture_placeholder.png"
                :alt="altText"
                class="profile-image"
            />
            <div v-if="editable" class="upload-overlay">
                <span class="upload-text">Ajouter</span>
            </div>
        </div>
        
        <!-- Loader pendant l'upload -->
        <div 
            v-if="loading"
            class="profile-picture profile-picture-loader"
            :class="`size-${size}`"
        >
            <div class="loader-container">
                <div class="loader">
                    <span style="--index: 1"></span>
                    <span style="--index: 2"></span>
                    <span style="--index: 3"></span>
                    <span style="--index: 4"></span>
                </div>
            </div>
        </div>
        
        <!-- Input file caché -->
        <input 
            ref="fileInput"
            type="file"
            accept="image/*"
            @change="handleFileUpload"
            style="display: none;"
        />
    </div>
</template>

<script>
import { getProfileMedia, revokeProfileMediaUrl, uploadProfilePicture } from '../../../services/profileMediaService'

export default {
    name: 'ProfilePicture',
    props: {
        mediaPath: {
            type: String,
            default: null
        },
        fullName: {
            type: String,
            default: ''
        },
        size: {
            type: String,
            default: 'medium', // 'small', 'medium', 'large', 'xlarge', 'full'
            validator: value => ['small', 'medium', 'large', 'xlarge', 'full'].includes(value)
        },
        altText: {
            type: String,
            default: 'Photo de profil'
        },
        editable: {
            type: Boolean,
            default: true
        },
        profileId: {
            type: Number,
            required: true
        }
    },
    emits: ['upload-success', 'upload-error'],
    data() {
        return {
            imageUrl: null,
            imageError: false,
            loading: false
        }
    },
    computed: {
        isSSO() {
			return this.$store.getters.isSSO
		},
    },
    methods: {
        async loadProfileMedia() {
            if (!this.mediaPath) {
                this.imageError = true
                return
            }

            try {
                this.loading = true
                this.imageError = false
                
                // Nettoyer l'ancienne URL blob si elle existe
                if (this.imageUrl) {
                    revokeProfileMediaUrl(this.imageUrl)
                }
                
                this.imageUrl = await getProfileMedia(this.mediaPath)
                
            } catch (error) {
                console.error('Erreur lors du chargement de l\'image de profil:', error)
                this.imageError = true
                this.imageUrl = null
            } finally {
                this.loading = false
            }
        },
        handleImageError() {
            this.imageError = true
        },
        triggerFileUpload() {
            this.$refs.fileInput.click();
        },
        async handleFileUpload(event) {
            const file = event.target.files[0];
            
            if (!file) {
                return;
            }

            try {
                this.loading = true;
                this.imageError = false;

                // Nettoyer l'ancienne URL blob si elle existe
                if (this.imageUrl) {
                    revokeProfileMediaUrl(this.imageUrl);
                }

                // Upload du fichier
                const result = await uploadProfilePicture(file, this.profileId);
                
                // Créer une URL temporaire pour l'affichage immédiat
                this.imageUrl = URL.createObjectURL(file);
                
                // Émettre l'événement de succès avec les données du serveur
                this.$emit('upload-success', {
                    file: file,
                    result: result,
                    tempUrl: this.imageUrl
                });

                // Charger immédiatement la nouvelle image depuis le serveur
                try {
                    // Nettoyer l'URL blob temporaire
                    if (this.imageUrl) {
                        revokeProfileMediaUrl(this.imageUrl);
                    }
                    
                    // Charger la nouvelle image depuis le serveur
                    this.imageUrl = await getProfileMedia(result);
                    this.imageError = false;
                } catch (error) {
                    console.error('Erreur lors du chargement de la nouvelle image:', error);
                    // En cas d'erreur, garder l'URL blob temporaire
                    this.imageUrl = URL.createObjectURL(file);
                }

            } catch (error) {
                console.error('Erreur lors de l\'upload de l\'image:', error);
                this.imageError = true;
                this.imageUrl = null;
                
                // Émettre l'événement d'erreur
                this.$emit('upload-error', error);
            } finally {
                this.loading = false;
                event.target.value = null; // Clear the file input
            }
        }
    },
    watch: {
        mediaPath: {
            immediate: true,
            handler(newPath) {
                if (newPath) {
                    this.loadProfileMedia()
                } else {
                    this.imageError = true
                    this.imageUrl = null
                }
            }
        }
    },
    mounted() {
    },
    beforeUnmount() {
        // Nettoyer l'URL blob lors de la destruction du composant
        if (this.imageUrl) {
            revokeProfileMediaUrl(this.imageUrl)
        }
    }
}
</script>

<style scoped lang="scss">
@import '../../../assets/scss/_variable.scss';
@import '../../../assets/scss/loader.scss';

.profile-picture-container {
    display: inline-block;
}

.profile-picture {
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    border: 3px solid #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    position: relative; /* Added for overlay positioning */
}

.profile-picture.editable {
    cursor: pointer; /* Added for clickability only when editable */
}

.profile-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.upload-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    border-radius: 8px;
}

.profile-picture:hover .upload-overlay {
    opacity: 1;
}

.upload-text {
    color: #fff;
    font-size: 14px;
    font-weight: bold;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

/* Loader styles */
.profile-picture-loader {
    background-color: #f7fafc;
    border: 2px dashed #cbd5e0;
}

.loader-container {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.loader {
    height: 30px;
    display: flex;
    align-items: flex-end;
}

.loader span {
    height: 6px;
    width: 9px;
    background-color: #553c9a;
    margin-right: 0.5rem;
    display: inline-block;
    animation: grow 1.5s ease infinite;
    animation-delay: calc(var(--index) * 0.1s);
}

@keyframes grow {
    0% {
        height: 6px;
    }
    50% {
        height: 24px;
    }
    100% {
        height: 6px;
    }
}

/* Tailles */
.profile-picture {
    &.size-small {
        width: 50px;
        height: 50px;
    }
    
    &.size-medium {
        width: 80px;
        height: 80px;
    }
    
    &.size-large {
        width: 120px;
        height: 120px;
    }
    
    &.size-xlarge {
        width: 150px;
        height: 150px;
    }

    &.size-full {
        width: 100%;
    }
}

/* Responsive */
@media (max-width: 768px) {
    .profile-picture {
        &.size-large {
            width: 80px;
            height: 80px;
        }
        
        &.size-xlarge {
            width: 100px;
            height: 100px;
        }
    }
}
</style> 